import { useEffect, useState } from "react";
import { Users, Briefcase, FileBox, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import StatusPill from "@/components/StatusPill";
import { formatINR } from "@/lib/utils";
import { ADMIN } from "@/constants/testIds";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

export default function AdminDashboardPage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setData(data)); }, []);
  if (!data) return <LoadingScreen />;

  const kpis = [
    { label: "Total Users", value: data.users.total, icon: Users, hint: `+${data.users.new_30d} in 30d` },
    { label: "Total Jobs", value: data.jobs.total, icon: Briefcase },
    { label: "Samples processed", value: data.samples.total, icon: FileBox },
    { label: "Revenue", value: formatINR(data.revenue.total_inr), icon: TrendingUp, hint: `${data.revenue.credits_sold} credits sold` },
  ];

  const chartData = Object.entries(data.jobs.by_status || {}).map(([k, v]) => ({
    status: k.replace(/_/g, " "), count: v,
  }));

  return (
    <div data-testid={ADMIN.dashboard} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="sqit-card p-5" data-testid={`admin-kpi-${k.label.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="flex justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{k.label}</div>
                  <div className="mt-2 font-display text-3xl font-bold text-gray-900">{k.value}</div>
                  {k.hint && <div className="text-xs text-gray-500 mt-1">{k.hint}</div>}
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#0B6936] flex items-center justify-center text-white">
                  <Icon size={18} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="sqit-card p-6 lg:col-span-2">
          <h3 className="font-display font-semibold text-gray-900 mb-4">Jobs by status</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="status" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                <Bar dataKey="count" fill="#0B6936" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="sqit-card p-6">
          <h3 className="font-display font-semibold text-gray-900 mb-4">Breakdown</h3>
          <div className="space-y-3">
            {Object.entries(data.jobs.by_status || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <StatusPill status={k} />
                <span className="text-sm font-semibold text-gray-900">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
